import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../../../shared/FormDialog";
import type { OrganisationHierarchy } from "../../../../api/organisations.types";
import { useEditTenant } from "../../../../api/tenants";

type FormValues = { organizationName: string; organizationNumber: string };
type EditTenantDialogProps = {
  tenant: OrganisationHierarchy;
  onClose: () => void;
  defaultValues: FormValues;
};

export const EditTenantDialog: FC<EditTenantDialogProps> = ({ tenant, onClose, defaultValues }) => {
  const { t } = useTranslation();
  const { mutate, isError, isPending } = useEditTenant();
  const { handleSubmit, reset, control, setFocus } = useForm<FormValues>({ defaultValues });

  FormDialog.useAutoFocusField(setFocus, "organizationName");

  const editTenant = ({ organizationName, organizationNumber }: FormValues) => {
    mutate(
      { payload: { organizationName, organizationNumber }, tenantId: tenant.id },
      {
        onSuccess: handleClose,
      }
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{t("OrganisationStructure.EditForm.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(editTenant)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="organizationName"
              rules={{ required: t("OrganisationStructure.EditForm.NameValidation") }}
              label={t("OrganisationStructure.EditForm.NameLabel")}
              placeholder={t("OrganisationStructure.AddChildForm.NamePlaceholder")}
            />

            <FormDialog.TextField
              control={control}
              name="organizationNumber"
              rules={{ required: t("OrganisationStructure.EditForm.OrgNumberValidation") }}
              label={t("OrganisationStructure.EditForm.OrgNumberLabel")}
              placeholder={t("OrganisationStructure.AddChildForm.OrgNumberPlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleClose} />
          <FormDialog.SubmitButton isSubmitting={isPending}>
            {t("OrganisationStructure.EditForm.Submit")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
